import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Hammer, Clock, CheckCircle, AlertCircle, Plus, Search, MapPin, Calendar, Users, Edit, Trash2, Eye, X } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import { cn } from '../utils/formatters';
import { CardSkeleton } from '../components/ui/LoadingScreen';
import { input, label, btnGhost, modalOverlay, modalContent, modalHeader, modalBody, modalFooter, formGroup } from '../styles/components';
import type { ProjectStatus } from '../types';

const statusConfig: Record<ProjectStatus, { label: string; pill: string; icon: typeof Clock }> = {
  planned: { label: 'Planificado', pill: '', icon: Clock },
  in_progress: { label: 'En curso', pill: 'b2b-pill--accent', icon: Hammer },
  stopped: { label: 'Detenido', pill: 'b2b-pill--warn', icon: AlertCircle },
  finished: { label: 'Finalizado', pill: 'b2b-pill--ok', icon: CheckCircle },
};

const statusOptions: { value: ProjectStatus; label: string }[] = [
  { value: 'planned', label: 'Planificado' },
  { value: 'in_progress', label: 'En curso' },
  { value: 'stopped', label: 'Detenido' },
  { value: 'finished', label: 'Finalizado' },
];

type ProjectFormState = {
  clientId: string;
  name: string;
  description: string;
  address: string;
  status: ProjectStatus;
  startDate: string;
  endDate: string;
  estimatedDays: string;
};

const emptyForm: ProjectFormState = {
  clientId: '',
  name: '',
  description: '',
  address: '',
  status: 'planned',
  startDate: '',
  endDate: '',
  estimatedDays: '',
};

export function ProjectsPage() {
  const { projects, clients, createProject, updateProject, deleteProject, isLoading } = useCompany();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ProjectStatus>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<ProjectFormState>(emptyForm);

  const canEdit = user?.role === 'admin' || user?.role === 'superadmin';

  const filteredProjects = projects.filter(p => {
    const clientName = clients.find(c => c.id === p.clientId)?.name.toLowerCase() || '';
    const matchesSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.address.toLowerCase().includes(search.toLowerCase()) ||
      clientName.includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (id: string) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;
    setEditingId(id);
    setForm({
      clientId: project.clientId,
      name: project.name,
      description: project.description || '',
      address: project.address,
      status: project.status,
      startDate: project.startDate ? project.startDate.split('T')[0] : '',
      endDate: project.endDate ? project.endDate.split('T')[0] : '',
      estimatedDays: project.estimatedDays?.toString() || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data = {
        clientId: form.clientId,
        name: form.name,
        description: form.description || undefined,
        address: form.address,
        status: form.status,
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
        estimatedDays: form.estimatedDays ? parseInt(form.estimatedDays) : undefined,
        assignedWorkers: [] as string[],
      };
      if (editingId) {
        await updateProject(editingId, data);
      } else {
        await createProject(data);
      }
      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('¿Eliminar este proyecto? Se perderán todos sus datos asociados.')) {
      await deleteProject(id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Proyectos</h1>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => <CardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Obras</span>
          <h1 className="text-3xl font-extrabold tracking-tight">Proyectos</h1>
          <p className="b2b-muted">Gestiona el ciclo de vida de tus obras</p>
        </div>
        {canEdit && (
          <button onClick={openCreate} className="b2b-btn b2b-btn--primary">
            <Plus className="w-4 h-4" /> Nuevo Proyecto
          </button>
        )}
      </div>

      <div className="b2b-glass flex flex-col sm:flex-row gap-3 p-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, dirección, cliente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={cn(input, 'pl-10')}
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as 'all' | ProjectStatus)}
          className={cn(input, 'sm:w-auto sm:min-w-[180px]')}
        >
          <option value="all">Todos los estados</option>
          {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <div className="b2b-panel">
        {filteredProjects.length === 0 ? (
          <div className="p-12 text-center">
            <Hammer className="w-16 h-12 mx-auto mb-3 opacity-30" />
            <h3 className="text-lg font-semibold">No hay proyectos</h3>
            <p className="b2b-muted mb-4">Empieza creando tu primera obra</p>
            {canEdit && (
              <button onClick={openCreate} className="b2b-btn b2b-btn--primary">
                <Plus className="w-4 h-4" /> Crear proyecto
              </button>
            )}
          </div>
        ) : (
          filteredProjects.map(project => {
            const client = clients.find(c => c.id === project.clientId);
            const config = statusConfig[project.status];
            const StatusIcon = config.icon;
            return (
              <Link key={project.id} to={`/projects/${project.id}`} className="b2b-panel__row">
                <span className="b2b-icon"><StatusIcon className="w-5 h-5" /></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{project.name}</span>
                    <span className={cn('b2b-pill', config.pill, 'whitespace-nowrap')}>{config.label}</span>
                  </div>
                  <span className="block text-sm b2b-muted truncate">{client?.name || 'Sin cliente'}</span>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs b2b-muted">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {project.address}</span>
                    {project.estimatedDays && (
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {project.estimatedDays} días</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-secondary-500">
                  {project.assignedWorkers?.length > 0 && (
                    <span className="text-sm flex items-center gap-1">
                      <Users className="w-3 h-3" /> {project.assignedWorkers.length}
                    </span>
                  )}
                  {canEdit && (
                    <>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(project.id); }}
                        className="p-2 rounded-lg hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)]"
                        aria-label="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(project.id); }}
                        className="p-2 rounded-lg text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <Eye className="w-4 h-4" />
                </div>
              </Link>
            );
          })
        )}
      </div>

      {showModal && (
        <div className={modalOverlay} onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="project-modal-title">
          <div className={cn(modalContent, 'w-full max-w-2xl')} onClick={e => e.stopPropagation()}>
            <div className={modalHeader}>
              <h2 id="project-modal-title" className="text-lg font-semibold">
                {editingId ? 'Editar proyecto' : 'Nuevo proyecto'}
              </h2>
              <button onClick={closeModal} className="p-1 rounded-lg text-secondary-500 hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)]" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form id="project-form" onSubmit={handleSubmit} className={cn(modalBody, 'space-y-4')} noValidate>
              <div className={formGroup}>
                <label htmlFor="clientId" className={label}>Cliente *</label>
                <select id="clientId" className={input} value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} required>
                  <option value="">Selecciona un cliente</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div className={formGroup}>
                <label htmlFor="name" className={label}>Nombre del proyecto *</label>
                <input id="name" type="text" className={input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: Reforma baño completa" required />
              </div>

              <div className={formGroup}>
                <label htmlFor="address" className={label}>Dirección de la obra *</label>
                <input id="address" type="text" className={input} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Calle, número, ciudad" required />
              </div>

              <div className={formGroup}>
                <label htmlFor="description" className={label}>Descripción</label>
                <textarea id="description" className={cn(input, 'min-h-[80px]')} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Detalles del alcance, materiales..." />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className={formGroup}>
                  <label htmlFor="status" className={label}>Estado</label>
                  <select id="status" className={input} value={form.status} onChange={e => setForm({ ...form, status: e.target.value as ProjectStatus })}>
                    {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className={formGroup}>
                  <label htmlFor="estimatedDays" className={label}>Días estimados</label>
                  <input id="estimatedDays" type="number" className={input} value={form.estimatedDays} onChange={e => setForm({ ...form, estimatedDays: e.target.value })} min="1" placeholder="30" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className={formGroup}>
                  <label htmlFor="startDate" className={label}>Fecha inicio estimada</label>
                  <input id="startDate" type="date" className={input} value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
                </div>
                <div className={formGroup}>
                  <label htmlFor="endDate" className={label}>Fecha fin estimada</label>
                  <input id="endDate" type="date" className={input} value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} />
                </div>
              </div>
            </form>
            <div className={modalFooter}>
              <button type="button" onClick={closeModal} className={btnGhost}>Cancelar</button>
              <button type="submit" form="project-form" className="b2b-btn b2b-btn--primary disabled:opacity-50" disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : (editingId ? 'Actualizar' : 'Crear')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
